import type { GPConfig, GPProblem } from "../types"
import { ConfigError, ProblemError } from "./errors"

/**
 * Validate GPConfig values and cross-field invariants.
 *
 * Highlights:
 * - Probabilities must be in [0,1].
 * - If both cxProb and mutProb are provided, they must sum to 1 (immigration is separate).
 * - Elite and population sizes must be non-negative integers; elite ≤ popSize is checked at engine construction time.
 * - Time/evaluation budgets must be finite and ≥ 0.
 */
export function validateConfig<T>(cfg: GPConfig<T>): void {
  if (
    cfg.popSize !== undefined &&
    (cfg.popSize < 2 || !Number.isInteger(cfg.popSize))
  ) {
    throw new ConfigError(
      "Population size must be an integer >= 2",
      "E_CFG_POPSIZE",
    )
  }
  if (
    cfg.generations !== undefined &&
    (cfg.generations < 1 || !Number.isInteger(cfg.generations))
  ) {
    throw new ConfigError(
      "Generations must be an integer >= 1",
      "E_CFG_GENERATIONS",
    )
  }
  if (
    cfg.elite !== undefined &&
    (cfg.elite < 0 || !Number.isInteger(cfg.elite))
  ) {
    throw new ConfigError(
      "Elite count must be a non-negative integer",
      "E_CFG_ELITE",
    )
  }
  if (cfg.cxProb !== undefined && (cfg.cxProb < 0 || cfg.cxProb > 1)) {
    throw new ConfigError(
      "Crossover probability must be between 0 and 1",
      "E_CFG_CXPROB",
    )
  }
  if (cfg.mutProb !== undefined && (cfg.mutProb < 0 || cfg.mutProb > 1)) {
    throw new ConfigError(
      "Mutation probability must be between 0 and 1",
      "E_CFG_MUTPROB",
    )
  }
  if (
    cfg.immigration !== undefined &&
    (cfg.immigration < 0 || cfg.immigration > 1)
  ) {
    throw new ConfigError(
      "Immigration rate must be between 0 and 1",
      "E_CFG_IMMIGRATION",
    )
  }
  // Operation proportions: enforce breeding operator sum to 1 within epsilon
  // Only validate the sum when BOTH cxProb and mutProb are explicitly provided.
  if (cfg.cxProb !== undefined && cfg.mutProb !== undefined) {
    const sum = cfg.cxProb + cfg.mutProb
    if (Math.abs(sum - 1) > 1e-6) {
      throw new ConfigError(
        "cxProb + mutProb must sum to 1 (immigration is separate)",
        "E_CFG_PROPORTIONS_SUM",
      )
    }
  }
  if (
    cfg.tournament !== undefined &&
    (cfg.tournament < 1 || !Number.isInteger(cfg.tournament))
  ) {
    throw new ConfigError(
      "Tournament size must be an integer >= 1",
      "E_CFG_TOURNAMENT",
    )
  }
  if (
    cfg.maxWallMs !== undefined &&
    (cfg.maxWallMs < 0 || !Number.isFinite(cfg.maxWallMs))
  ) {
    throw new ConfigError(
      "maxWallMs must be a finite number >= 0",
      "E_CFG_MAX_WALL",
    )
  }
  if (
    cfg.maxEvaluations !== undefined &&
    (cfg.maxEvaluations < 0 || !Number.isFinite(cfg.maxEvaluations))
  ) {
    throw new ConfigError(
      "maxEvaluations must be a finite number >= 0",
      "E_CFG_MAX_EVALS",
    )
  }
  if (
    cfg.concurrency !== undefined &&
    (!Number.isInteger(cfg.concurrency) || cfg.concurrency < 1)
  ) {
    throw new ConfigError(
      "concurrency must be an integer >= 1",
      "E_CFG_CONCURRENCY",
    )
  }
  if (
    cfg.fitnessTimeoutMs !== undefined &&
    (cfg.fitnessTimeoutMs < 0 || !Number.isFinite(cfg.fitnessTimeoutMs))
  ) {
    throw new ConfigError(
      "fitnessTimeoutMs must be a finite number >= 0",
      "E_CFG_FITNESS_TIMEOUT",
    )
  }
  if (
    cfg.stall !== undefined &&
    (cfg.stall < 0 || !Number.isInteger(cfg.stall))
  ) {
    throw new ConfigError("stall must be a non-negative integer", "E_CFG_STALL")
  }
  if (
    cfg.targetFitness !== undefined &&
    !Number.isFinite(cfg.targetFitness) &&
    cfg.targetFitness !== Infinity
  ) {
    throw new ConfigError(
      "targetFitness must be a finite number or Infinity",
      "E_CFG_TARGET_FITNESS",
    )
  }
}

export function validateProblem<T>(problem: GPProblem<T>): void {
  if (!problem.fitness) {
    throw new ProblemError(
      "Problem must have a fitness function",
      "E_PROBLEM_FITNESS",
    )
  }
  if (!problem.createRandom) {
    throw new ProblemError(
      "Problem must have a createRandom function",
      "E_PROBLEM_CREATE",
    )
  }
  if (!problem.crossover) {
    throw new ProblemError(
      "Problem must have a crossover function",
      "E_PROBLEM_CROSSOVER",
    )
  }
  if (!problem.mutate) {
    throw new ProblemError(
      "Problem must have a mutate function",
      "E_PROBLEM_MUTATE",
    )
  }
}
